import clsx from 'clsx';
import React from 'react';

import Bookmark from '@components/Bookmark';
import { TextButton } from '@components/Button/variants';
import BylineWithHeadshot from '@components/BylineWithHeadshot';
import ClientRenderedContent from '@components/ClientRenderedContent';
import IconLink from '@components/Images/Rebranding/IconLink';
import ListenButton from '@components/ListenButton';
import useEventTracking from '@hooks/useEventTracking';
import useNativeAppVersionCheck from '@hooks/useNativeAppVersionCheck';
import type { Author } from '@interfaces/authors';
import { ClickTarget, ContentType, click } from '@tracking';

import type { BriefingSource } from './index';

interface BriefingDetailProps {
  authors: Author[];
  canViewFullBriefing: boolean;
  id: number;
  isAudioActive: boolean;
  publishedAt: string;
  setIsAudioActive: (isAudioActive: boolean) => void;
  source: BriefingSource;
}

const BriefingDetails = ({
  authors,
  canViewFullBriefing,
  isAudioActive,
  id,
  publishedAt,
  setIsAudioActive,
  source,
}: BriefingDetailProps) => {
  const { trackEvent } = useEventTracking();
  const { isApp3_0OrGreater, isNativeApp } = useNativeAppVersionCheck();

  const showListenButton = !isNativeApp || (isNativeApp && isApp3_0OrGreater);

  return (
    <div
      className={clsx('briefing-detail flex max-w-[350px] flex-col', {
        'pointer-events-none opacity-60': !canViewFullBriefing,
      })}
    >
      <BylineWithHeadshot
        authors={authors}
        authorClassName="author-name-body"
        className="max-w-sm"
      />
      <div className="mt-24 flex gap-x-16 self-end">
        {showListenButton && (
          <ListenButton
            contentType={ContentType.Briefing}
            isAudioActive={isAudioActive}
            setIsAudioActive={setIsAudioActive}
          />
        )}
        <Bookmark contentId={id} contentType={ContentType.Briefing} />
      </div>

      <div className="border-grayscale-gra w-full border-0 border-b border-solid border-grayscale-gray pt-24" />
      <div
        className={clsx(
          'mt-8 flex flex-wrap items-center justify-between gap-24 text-grayscale-gray lg:items-start',
          { 'opacity-50': !canViewFullBriefing }
        )}
      >
        <ClientRenderedContent>
          <time
            className="block font-suisse-intl-mono text-overline-md text-grayscale-gray lg:text-overline-sm xl:text-overline-md"
            dateTime={publishedAt}
          >
            {publishedAt}
          </time>
        </ClientRenderedContent>
        <div className="mr-16 flex items-center">
          <IconLink className="h-16 w-16 text-grayscale-gray" />
          <span className="px-8 font-suisse-intl-mono text-overline-sm text-grayscale-gray">
            Source:
          </span>
          <TextButton
            as="link"
            href={source.url}
            className={clsx(
              '!w-3/5 font-suisse-intl text-icon-sm text-grayscale-gray no-underline',
              { 'opacity-50': !canViewFullBriefing }
            )}
            onClickCapture={() =>
              trackEvent(
                click({
                  clickLink: source.url,
                  clickTarget: ClickTarget.BriefingSource,
                  clickText: source.name,
                })
              )
            }
          >
            {source.name}
          </TextButton>
        </div>
      </div>
    </div>
  );
};

export default BriefingDetails;
